<template>
  <div
    v-if="hasSecondaryMenu"
    class="h-full overflow-auto w-52 flex flex-col bg-white dark:bg-slate-900 border-r dark:border-slate-800/50 rtl:border-r-0 rtl:border-l border-slate-50 text-sm px-2 pb-8"
  >
    <account-context @toggle-accounts="toggleAccountModal" />
    <transition-group
      name="menu-list"
      tag="ul"
      class="pt-2 list-none ml-0 mb-0"
    >
      <secondary-nav-item
        v-for="menuItem in accessibleMenuItems"
        :key="menuItem.toState"
        :menu-item="menuItem"
      />

      <groups-manager
        v-if="menuConfig.parentNav === 'conversations'"
        key="groups"
        :teams="teams"
        :inboxes="inboxes"
        :labels="labels"
        :can-create-label="showNewLink(FEATURE_FLAGS.TEAM_MANAGEMENT)"
        @add-label="showAddLabelPopup"
      />

      <secondary-nav-item
        v-for="menuItem in additionalSecondaryMenuItems[menuConfig.parentNav]"
        :key="menuItem.key"
        :menu-item="menuItem"
        @add-label="showAddLabelPopup"
      />
    </transition-group>
  </div>
</template>

<script>
import { frontendURL } from '../../../helper/URLHelper';
import SecondaryNavItem from './SecondaryNavItem.vue';
import AccountContext from './AccountContext.vue';
import GroupDropdown from './groups/GroupDropdown.vue';
import Group from './groups/Group.vue';
import GroupItem from './groups/GroupItem.vue';
import GroupContextMenu from './groups/GroupContextMenu.vue';
import CreateGroupModal from './groups/CreateGroupModal';
import DeleteConfirmationModal from './groups/DeleteConfirmationModal';
import EditGroupModal from './groups/EditGroupModal';
import { mapGetters } from 'vuex';
import { FEATURE_FLAGS } from '../../../featureFlags';
import { hasPermissions } from '../../../helper/permissionsHelper';
import { routesWithPermissions } from '../../../routes';
import adminMixin from 'dashboard/mixins/isAdmin';
import GroupsManager from './groups/Index.vue';

export default {
  components: {
    AccountContext,
    SecondaryNavItem,
    GroupDropdown,
    Group,
    GroupItem,
    GroupContextMenu,
    CreateGroupModal,
    DeleteConfirmationModal,
    EditGroupModal,
    GroupsManager,
  },
  mixins: [adminMixin],
  props: {
    accountId: {
      type: Number,
      default: 0,
    },
    labels: {
      type: Array,
      default: () => [],
    },
    inboxes: {
      type: Array,
      default: () => [],
    },
    teams: {
      type: Array,
      default: () => [],
    },
    customViews: {
      type: Array,
      default: () => [],
    },
    menuConfig: {
      type: Object,
      default: () => {},
    },
    currentUser: {
      type: Object,
      default: () => {},
    },
    isOnChatwootCloud: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      FEATURE_FLAGS,
      contextMenu: {
        isVisible: false,
        position: { x: 0, y: 0 },
        itemType: '',
        itemId: null,
      },
      showCreateGroupModal: false,
      showEditModal: false,
      selectedGroup: null,
      selectedGroupType: '',
      showDeleteModal: false,
      groupToDelete: null,
    };
  },
  watch: {
    groups: {
      handler(newGroups) {
      },
      immediate: true
    }
  },
  computed: {
    ...mapGetters({
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
      groups: 'groups/getGroups',
    }),
    hasSecondaryMenu() {
      return this.menuConfig.menuItems && this.menuConfig.menuItems.length;
    },
    contactCustomViews() {
      return this.customViews.filter(view => view.filter_type === 'contact');
    },
    accessibleMenuItems() {
      const menuItemsFilteredByPermissions = this.menuConfig.menuItems.filter(
        menuItem => {
          const { permissions: userPermissions = [] } = this.currentUser;
          return hasPermissions(
            routesWithPermissions[menuItem.toStateName],
            userPermissions
          );
        }
      );
      return menuItemsFilteredByPermissions.filter(item => {
        if (item.showOnlyOnCloud) {
          return this.isOnChatwootCloud;
        }
        return true;
      });
    },
    inboxSection() {
      return {
        icon: 'folder',
        label: 'INBOXES',
        hasSubMenu: true,
        newLink: this.showNewLink(FEATURE_FLAGS.INBOX_MANAGEMENT),
        newLinkTag: 'NEW_INBOX',
        key: 'inbox',
        toState: frontendURL(`accounts/${this.accountId}/settings/inboxes/new`),
        toStateName: 'settings_inbox_new',
        newLinkRouteName: 'settings_inbox_new',
        children: this.inboxes
          .map(inbox => ({
            id: inbox.id,
            label: inbox.name,
            truncateLabel: true,
            toState: frontendURL(
              `accounts/${this.accountId}/inbox/${inbox.id}`
            ),
            type: inbox.channel_type,
            phoneNumber: inbox.phone_number,
            reauthorizationRequired: inbox.reauthorization_required,
          }))
          .sort((a, b) =>
            a.label.toLowerCase() > b.label.toLowerCase() ? 1 : -1
          ),
      };
    },
    contactLabelSection() {
      return {
        icon: 'number-symbol',
        label: 'TAGGED_WITH',
        hasSubMenu: true,
        key: 'label',
        newLink: this.showNewLink(FEATURE_FLAGS.TEAM_MANAGEMENT),
        newLinkTag: 'NEW_LABEL',
        toState: frontendURL(`accounts/${this.accountId}/settings/labels`),
        toStateName: 'labels_list',
        showModalForNewItem: true,
        modalName: 'AddLabel',
        children: this.labels.map(label => ({
          id: label.id,
          label: label.title,
          color: label.color,
          truncateLabel: true,
          toState: frontendURL(
            `accounts/${this.accountId}/labels/${label.title}/contacts`
          ),
        })),
      };
    },
    teamSection() {
      return {
        icon: 'people-team',
        label: 'TEAMS',
        hasSubMenu: true,
        newLink: this.showNewLink(FEATURE_FLAGS.TEAM_MANAGEMENT),
        newLinkTag: 'NEW_TEAM',
        key: 'team',
        toState: frontendURL(`accounts/${this.accountId}/settings/teams/new`),
        toStateName: 'settings_teams_new',
        newLinkRouteName: 'settings_teams_new',
        children: this.teams.map(team => ({
          id: team.id,
          label: team.name,
          truncateLabel: true,
          toState: frontendURL(`accounts/${this.accountId}/team/${team.id}`),
        })),
      };
    },
    foldersSection() {
      return {
        icon: 'folder',
        label: 'CUSTOM_VIEWS_FOLDER',
        hasSubMenu: true,
        key: 'custom_view',
        children: this.customViews
          .filter(view => view.filter_type === 'conversation')
          .map(view => ({
            id: view.id,
            label: view.name,
            truncateLabel: true,
            toState: frontendURL(
              `accounts/${this.accountId}/custom_view/${view.id}`
            ),
          })),
      };
    },
    contactSegmentsSection() {
      return {
        icon: 'folder',
        label: 'CUSTOM_VIEWS_SEGMENTS',
        hasSubMenu: true,
        key: 'custom_view',
        children: this.customViews
          .filter(view => view.filter_type === 'contact')
          .map(view => ({
            id: view.id,
            label: view.name,
            truncateLabel: true,
            toState: frontendURL(
              `accounts/${this.accountId}/contacts/custom_view/${view.id}`
            ),
          })),
      };
    },
    additionalSecondaryMenuItems() {
      // Conversation labels are rendered by the groups manager, which supports folders
      let conversationMenuItems = [];
      let contactMenuItems = [this.contactLabelSection];
      
      if (this.contactCustomViews.length) {
        contactMenuItems = [this.contactSegmentsSection, ...contactMenuItems];
      }
      
      return {
        conversations: conversationMenuItems,
        contacts: contactMenuItems,
      };
    },
    teamGroups() {
      return this.groups
        .filter(group => group.group_type === 'team')
        .map(group => ({
          ...group,
          items: group.items.filter(item => this.teams.some(t => t.id === item.id))
        }));
    },

    inboxGroups() {
      return this.groups
        .filter(group => group.group_type === 'inbox')
        .map(group => ({
          ...group,
          items: group.items.filter(item => this.inboxes.some(i => i.id === item.id))
        }));
    },
    ungroupedTeams() {
      const groupedTeamIds = this.teamGroups
        .flatMap(group => group.items)
        .map(item => item.id);
      return this.teams.filter(team => !groupedTeamIds.includes(team.id));
    },

    ungroupedInboxes() {
      const groupedInboxIds = this.inboxGroups
        .flatMap(group => group.items)
        .map(item => item.id);
      return this.inboxes.filter(inbox => !groupedInboxIds.includes(inbox.id));
    },
  },
  methods: {
    showAddLabelPopup() {
      this.$emit('add-label');
    },
    toggleAccountModal() {
      this.$emit('toggle-accounts');
    },
    showNewLink(featureFlag) {
      return this.isFeatureEnabledonAccount(this.accountId, featureFlag);
    },
    createGroup(groupType) {
      this.$store.dispatch('groups/create', { group_type: groupType });
    },
    openCreateGroupModal(type) {
      this.selectedGroupType = type;
      this.showCreateGroupModal = true;
    },
    showContextMenu(event, { itemType = '', itemId = null } = {}) {
      this.contextMenu = {
        isVisible: true,
        position: {
          x: event.clientX,
          y: event.clientY,
        },
        itemType,
        itemId,
      };
    },

    closeContextMenu() {
      this.contextMenu.isVisible = false;
    },
    handleRename() {
      const group = this.groups.find(g => g.id === this.contextMenu.itemId);
      this.selectedGroup = group;
      this.showEditModal = true;
      this.closeContextMenu();
    },

    handleEmpty() {
      this.$store.dispatch('groups/clearItems', this.contextMenu.itemId);
    },
    handleDelete() {
      this.groupToDelete = this.contextMenu.itemId;
      this.showDeleteModal = true;
      this.closeContextMenu();
    },
    confirmDelete() {
      this.$store.dispatch('groups/delete', this.groupToDelete);
      this.groupToDelete = null;
    },
    handleAssign(data) {
			this.$store.dispatch('groups/update', {
				id: data.groupId,
				item_ids: [this.item.id]
			});
		},
    handleGroupDrop(e) {
      const data = JSON.parse(e.dataTransfer.getData('text/plain'));
      if (data.type === 'group') {
        this.$store.dispatch('groups/reorderGroups', {
          sourceId: data.id,
          targetPosition: this.getDropPosition(e)
        });
      }
    },
    getDropPosition(e) {
      const groups = this.$el.querySelectorAll('.group-container');
      const mouseY = e.clientY;
      let position = 0;

      groups.forEach((group, index) => {
        const rect = group.getBoundingClientRect();
        if (mouseY > rect.top + rect.height / 2) {
          position = index + 1;
        }
      });

      return position;
    }
  },
  mounted() {
		this.$store.dispatch('groups/get');
	},
  created() {
    this.selectedGroup = {};
  }
};
</script>